import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@parcel/ui';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';
import * as React from 'react';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { logoUrl } from '../../lib/logo.js';
import {
  type ProjectRow,
  formatDetected,
  formatSqft,
  stageMeta,
} from '../../lib/projects-display.js';
import type { Score } from '../../lib/scores.js';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.js';
import {
  DATA_TABLE_DEFAULT_PAGE_SIZE,
  type PinnedColumn,
} from '../data-table/data-table-layout.js';
import { EntityAvatar } from '../entity-drawer/entity-drawer-shell.js';
import { getProjectTypeIcon } from '../entity-drawer/project-type-icon.js';
import { scoreColumn } from '../scoring/score-column.js';
import { StatusBadge } from './status-badge.js';

export const PROJECTS_PINNED_COLUMNS: PinnedColumn[] = [
  { id: 'select', left: 0, width: 52 },
  { id: 'name', left: 52, width: 316, last: true },
];

function projectColumns(browseMode: boolean, atlasScore?: Score): ColumnDef<ProjectRow>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={
              browseMode
                ? table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && 'indeterminate')
                : table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              browseMode
                ? table.toggleAllRowsSelected(Boolean(value))
                : table.toggleAllPageRowsSelected(Boolean(value))
            }
            aria-label={browseMode ? 'Select all loaded projects' : 'Select all'}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${row.original.name}`}
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 52,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Project" />,
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-3">
          <EntityAvatar
            src={logoUrl(row.original.sourceLogoDomain)}
            alt={row.original.source}
            fallbackIcon={getProjectTypeIcon(row.original.type)}
            size="sm"
            href={normalizeExternalUrl(row.original.sourceBaseUrl)}
          />
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{row.original.name}</div>
          </div>
        </div>
      ),
      size: 316,
    },
    scoreColumn<ProjectRow>('project', atlasScore),
    {
      accessorKey: 'stage',
      header: 'Stage',
      cell: ({ row }) => <StatusBadge {...stageMeta(row.original.stage)} />,
      enableSorting: false,
      // Fits "Under construction" plus cell padding; narrower shows a trailing ellipsis.
      size: 176,
    },
    {
      accessorKey: 'city',
      header: 'Location',
      cell: ({ row }) => (
        <div>
          <div className="truncate">{row.original.city}</div>
          <div className="truncate text-xs text-muted-foreground">{row.original.state}</div>
        </div>
      ),
      enableSorting: false,
      size: 150,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <div>
          <div className="truncate">{row.original.type}</div>
          <div className="truncate text-xs text-muted-foreground">
            {formatSqft(row.original.sqft)}
          </div>
        </div>
      ),
      enableSorting: false,
      size: 140,
    },
    {
      // accessorKey stays `detectedAt` (it backs the default sort + saved views);
      // the value is last_signal_at, surfaced to users as "Last activity".
      accessorKey: 'detectedAt',
      meta: { label: 'last activity' },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Last activity" />,
      cell: ({ row }) => (
        <div>
          <div>{formatDetected(row.original.detectedAt)}</div>
          <div className="truncate text-xs text-muted-foreground">{row.original.source}</div>
        </div>
      ),
      size: 230,
    },
    {
      id: 'actions',
      enableHiding: false,
      // The 32px button and the cell padding overflowed 56px, which drew a second ellipsis.
      size: 64,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open row actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem>Open project</DropdownMenuItem>
            <DropdownMenuItem
              onClick={(event) => {
                event.stopPropagation();
                row.toggleSelected(true);
              }}
            >
              Add to View
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>{row.original.source}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
}

export const PROJECTS_DEFAULT_SORTING: SortingState = [{ id: 'detectedAt', desc: true }];

export interface ProjectsTableOptions {
  browseMode: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /** The workspace's Atlas Score, for its bucket labels. */
  atlasScore?: Score;
}

export function useProjectsTable(projects: ProjectRow[], options: ProjectsTableOptions) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { browseMode, atlasScore } = options;
  const columns = React.useMemo(
    () => projectColumns(browseMode, atlasScore),
    [browseMode, atlasScore],
  );

  const table = useReactTable({
    data: projects,
    columns,
    // Key rows by project id (not array index) so rows reorder instead of
    // re-mounting as the list grows or re-sorts while pages stream in.
    getRowId: (row) => row.id,
    state: {
      sorting: options.sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: DATA_TABLE_DEFAULT_PAGE_SIZE,
      },
    },
    enableRowSelection: true,
    onSortingChange: options.onSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: options.browseMode,
    getPaginationRowModel: options.browseMode ? undefined : getPaginationRowModel(),
    getSortedRowModel: options.browseMode ? undefined : getSortedRowModel(),
  });

  return { table };
}
